/**
 * 验证码字段 —— 契约 captcha.ts 的客户端一半:
 *
 * - 每个验证码表单挂载时取一次 challenge(GET /api/v1/auth/captcha,
 *   安全方法,不需要 CSRF);
 * - challenge 绑定一张渲染图且每次校验尝试即烧毁:提交失败
 *   (captcha_failed)后必须取新 challenge 再重试 —— 调用方在错误
 *   处理里调 refresh()( useCaptcha().reload );
 * - png_base64 是服务端渲染的光栅图(data:image/png;base64 URI):算式
 *   只存在于像素里(与旧站 mews/captcha math 一致),明文抓取响应拿不到
 *   答案;用普通 <img> 渲染,点击图形可刷新(旧站 captcha_reload 交互的
 *   延续)。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { CaptchaChallengeData } from '@web-v4/api-contract/v1/auth';
import { Input } from '@web-v4/shared-ui';
import { authClient } from '../lib/clients';

export interface CaptchaValue {
  /** 当前 challenge id;尚未取到 challenge 时为 undefined(提交应禁用)。 */
  readonly captchaId: string | undefined;
  /** 用户输入的答案(受控)。 */
  readonly answer: string;
}

export interface UseCaptchaResult {
  readonly challenge: CaptchaChallengeData | undefined;
  /** 取 challenge 失败(网络/后端)时的提示;成功或重试中为空。 */
  readonly loadError: string | undefined;
  /** 丢弃当前 challenge 并取新的(captcha_failed 后必须调用)。 */
  readonly reload: () => void;
}

export function useCaptcha(): UseCaptchaResult {
  const [challenge, setChallenge] = useState<CaptchaChallengeData | undefined>(undefined);
  const [loadError, setLoadError] = useState<string | undefined>(undefined);
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    setChallenge(undefined);
    setLoadError(undefined);
    authClient
      .captcha()
      .then((data) => {
        if (!cancelled) {
          setChallenge(data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLoadError('验证码加载失败,点击图形重试。');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [tick]);

  return { challenge, loadError, reload };
}

export interface CaptchaFieldProps {
  readonly captcha: UseCaptchaResult;
  readonly answer: string;
  readonly onAnswerChange: (answer: string) => void;
  /** 字段级错误(来自后端 fields.captcha,归一后)。 */
  readonly error?: string;
  readonly disabled?: boolean;
}

/**
 * 答案输入 + challenge 图形组合。布局:输入框在左,SVG 图形在右
 * (button 包裹,点击刷新);label/错误文案由调用方的 FormField 提供
 * 外层结构,这里只出自己的控件行。
 */
export function CaptchaControl({ captcha, answer, onAnswerChange, error, disabled }: CaptchaFieldProps) {
  const { challenge, loadError, reload } = captcha;
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div className="wv-portal-captcha">
      <Input
        ref={inputRef}
        type="text"
        name="captcha"
        placeholder="验证码"
        autoComplete="off"
        required
        value={answer}
        invalid={error !== undefined}
        disabled={disabled}
        onChange={(event) => onAnswerChange(event.target.value)}
      />
      <button
        type="button"
        className="wv-portal-captcha__image"
        title="点击刷新验证码"
        aria-label="刷新验证码"
        disabled={disabled}
        onClick={() => {
          reload();
          inputRef.current?.focus();
        }}
      >
        {challenge !== undefined && (
          <img src={challenge.png_base64} width={120} height={34} alt="验证码" />
        )}
      </button>
      {loadError !== undefined && (
        <p className="wv-field__error" role="alert">
          {loadError}
        </p>
      )}
      {error !== undefined && (
        <p className="wv-field__error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
